import * as Res from "./res";
import { Modal }  from "./_modal";
import { createElement } from "./_dom";
import { debounce } from "./debounce";

class Pointer {
	constructor({ x, y, width = 3, color = "black" }) {
		this.x = x;
		this.y = y;
		this.width = width;
		this.color = color;
	}

	setTarget({ x, y }) {
		this.targetX = x;
		this.targetY = y;
	}

	didReachTarget() {
		this.x = this.targetX;
		this.y = this.targetY;
	}

	isDelta() {
		return this.targetX && this.targetY &&
			(this.x != this.targetX || this.y != this.targetY);
	}
}

class Paint {
	constructor(canvas, options = {}) {
		this.options = { lineCap: "round", lineJoin: "round", ...options };
		const ctx = canvas.getContext("2d");
		ctx.lineCap = this.options.lineCap;
		ctx.lineJoin = this.options.lineJoin;
		ctx.fillStyle = "white";
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		this.ctx = ctx;
		this.isEmpty = true;
		this.canvas = canvas;
		this.lines = {};
		this.pointers = {};
		this.initEvents();
		requestAnimationFrame(this.renderLoop, 1000 / 60 /* fps */);
	}

	initEvents() {
		const canvas = this.canvas;
		canvas.addEventListener('pointerdown', this.onPointerDown);
		canvas.addEventListener('pointermove', this.onPointerMove);
		canvas.addEventListener('pointerup', this.onPointerUp);
		canvas.addEventListener('pointercancel', this.onPointerUp);
		canvas.addEventListener('pointerleave', this.onPointerUp);
	}

	getPointerCoords(event) {
		const { left, top } = this.canvas.getBoundingClientRect();
		return { x: event.clientX - left, y: event.clientY - top };
	}

	onPointerDown = event => {
		const minwidth = 2, maxwidth = 5;
		let width = event.pointerType === 'touch' ? (event.width || maxwidth) : maxwidth;
		width = Math.min(Math.max(minwidth, width), maxwidth);
		const { left, top } = this.canvas.getBoundingClientRect();
		this.pointers[event.pointerId] = new Pointer({ width, ...this.getPointerCoords(event) });
		event.preventDefault();
	}

	onPointerMove = event => {
		const pointer = this.pointers[event.pointerId];
		if (pointer) {
			pointer.setTarget(this.getPointerCoords(event));
		}
	}

	onPointerUp = event => {
		delete this.pointers[event.pointerId];
	}

	renderLoop = () => {
		var dx, dy;
		for (var pointerId in this.pointers) {
			var pointer = this.pointers[pointerId];
			if (pointer.isDelta()) {
				var ctx = this.ctx;
				if (!dx) {
					var c = this.canvas;
					var r = c.getBoundingClientRect();
					dx = c.width / r.width;
					dy = c.height / r.height;
				}
				ctx.lineWidth = pointer.width;
				ctx.strokeStyle = pointer.color;
				ctx.beginPath();
				ctx.moveTo(pointer.x * dx, pointer.y * dy);
				ctx.lineTo(pointer.targetX * dx, pointer.targetY * dy);
				ctx.stroke();
				ctx.closePath();
				this.isEmpty = false;
				pointer.didReachTarget();
			}
		}
		requestAnimationFrame(this.renderLoop);
	}

	clear() {
		this.ctx.fillStyle = "white";
		this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
		this.isEmpty = true;
	}
}

function isBlank(data, i) {
	return data[i] == 255 && data[i + 1] == 255 && data[i + 2] == 255;
}

function scanY(imageData, fromTop) {
	const w = imageData.width;
	const data = imageData.data;
	var b = 0, e = data.length, off = 4;
	if (!fromTop) {
		b = e - 4; e = -4; off = -4;
	}
	for (; b != e; b += off) {
		if (!isBlank(data, b)) {
			return (b / (w * 4)) | 0;
		}
	}
	return null;
}

function scanX(imageData, fromLeft) {
	var w = imageData.width * 4;
	var data = imageData.data;
	var l = data.length;
	var b = 0, e = w, off = 4;
	if (!fromLeft) {
		b = e - 4; e = -4; off = -4;
	}
	for (; b != e; b += off) {
		for (var i = b; i < l; i += w) {
			if (!isBlank(data, i)) {
				return (b / 4) | 0;
			}
		}
	}
	return null;
}

function removeBlanks(canvas) {
	var width = canvas.width, height = canvas.height;
	var imageData = canvas.getContext("2d").getImageData(0, 0, width, height);

	var top = scanY(imageData, true);
	if (top == null) return null;
	height = scanY(imageData, false) + 1 - top;
	var left = scanX(imageData, true);
	width = scanX(imageData, false) + 1 - left;

	var resampled = document.createElement("canvas");
	resampled.width = width;
	resampled.height = height;
	resampled.getContext("2d").drawImage(canvas, left, top, width, height, 0, 0, width, height);
	return resampled;
}


function onresize(canvas) {
	const resampled = removeBlanks(canvas);
	const p = canvas.parentElement;
	let dx = p.clientWidth;
	let dy = p.clientHeight;

	if (dx > 800 || dy > 800) {
		if (dx > dy) { // landscape
			dy *= 800 / dx;
			dx = 800;
		}
		else { // portrait
			dx *= 800 / dy;
			dy = 800;
		}
	}
	canvas.width = dx;
	canvas.height = dy;
	const ctx = canvas.getContext("2d");
	ctx.lineCap = "round";
	ctx.fillStyle = "white";
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	if (resampled != null) { // copy image
		const sx = resampled.width, sy = resampled.height;
		ctx.drawImage(resampled, 0, 0, sx, sy,
			(dx - sx) / 2, (dy - sy) / 2, sx, sy);
	}
}

const delayedOnResize = debounce(onresize, 100);


const resizeObserver = new ResizeObserver(entries => {
	entries.forEach(({ target }) => delayedOnResize(target));
});

const signature = (successCallback) => {
	/** @type {HTMLCanvasElement} */
	const canvas = createElement("canvas", { class: "v3-signature__pad" });
	// important, set as attribute
	canvas.setAttribute("width", "800");
	canvas.setAttribute("height", "800");

	const emptyDataUrl = canvas.toDataURL();

	const paint = new Paint(canvas);

	const doneButton = createElement("button", {
			type: "button",
			class: "v3-button ui-action-done",
			onclick: () => {
				if (!paint.isEmpty) {
					const resampled = removeBlanks(canvas);
					successCallback(resampled.toDataURL("image/png", 0));
				}
				signatureModal.removeModal();
			}
		},
		Res.getString("signature.action.done")
	);
	const resetButton = createElement("button", {
			type: "button",
			class: "v3-button ui-action-reset",
			onclick: () => paint.clear()
		},
		Res.getString("signature.action.reset")
	);

	const cancelButton = createElement("button", {
			type: "button",
			class: "v3-button ui-action-cancel",
			onclick: () => signatureModal.removeModal()
		}, 
		Res.getString("action.cancel")
	);

	const content = createElement("div", { class: "v3-dialog__content" },
		createElement('div', { class: "v3-signature__title" }, Res.getString("signature.title")),
		canvas,
		createElement("div", { class: "v3-signature__buttons v3-buttonbar" }, doneButton, resetButton, cancelButton));

	const signatureModal = new Modal(content).start();
	resizeObserver.observe(canvas);
};

export default signature;
